import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { type ListSigningDto } from 'xosign-types';
import styles from './FilterPills.module.scss';

export type SigningFilter =
  | 'draft'
  | 'pending'
  | 'done'
  | 'expired'
  | 'cancelled';

export const SIGNING_FILTERS: SigningFilter[] = [
  'draft',
  'pending',
  'done',
  'expired',
  'cancelled',
];

type PillDef = {
  key: 'all' | SigningFilter;
  label: string;
  count: number;
};

interface FilterPillsProps {
  activeFilters: SigningFilter[];
  counts: Record<'all' | SigningFilter, number>;
  onToggle: (filter: 'all' | SigningFilter) => void;
  className?: string;
}

const FilterPills = ({
  activeFilters,
  counts,
  onToggle,
  className,
}: FilterPillsProps): ReactNode => {
  const { t } = useTranslation(['signings']);

  const pills: PillDef[] = [
    { key: 'all', label: t('signings:title.all'), count: counts.all },
    { key: 'draft', label: t('signings:title.draft'), count: counts.draft },
    {
      key: 'pending',
      label: t('signings:title.pending'),
      count: counts.pending,
    },
    { key: 'done', label: t('signings:title.done'), count: counts.done },
    {
      key: 'expired',
      label: t('signings:title.expired'),
      count: counts.expired,
    },
    {
      key: 'cancelled',
      label: t('signings:title.cancelled'),
      count: counts.cancelled,
    },
  ];

  const allActive = activeFilters.length === 0;

  return (
    <nav
      className={classNames(styles.pills, className)}
      aria-label={t('signings:filter.label')}
    >
      {pills.map((pill) => {
        const isActive =
          pill.key === 'all' ? allActive : activeFilters.includes(pill.key);
        return (
          <button
            key={pill.key}
            type="button"
            className={classNames(styles.pill, { [styles.active]: isActive })}
            aria-pressed={isActive}
            onClick={() => onToggle(pill.key)}
          >
            <span className={styles.label}>{pill.label}</span>
            <span className={styles.count}>{pill.count}</span>
          </button>
        );
      })}
    </nav>
  );
};

export const computeCounts = (
  signings: ListSigningDto[]
): Record<'all' | SigningFilter, number> => {
  const counts: Record<'all' | SigningFilter, number> = {
    all: signings.length,
    draft: 0,
    pending: 0,
    done: 0,
    expired: 0,
    cancelled: 0,
  };

  for (const signing of signings) {
    if (signing.state === 'DRAFT') {
      counts.draft += 1;
    } else if (
      signing.state === 'IN_REVIEW' ||
      signing.state === 'STARTING' ||
      signing.state === 'SIGNING'
    ) {
      counts.pending += 1;
    } else if (signing.state === 'EXPIRED') {
      counts.expired += 1;
    } else if (signing.state === 'CANCELLED') {
      counts.cancelled += 1;
    } else {
      counts.done += 1;
    }
  }

  return counts;
};

export const matchesFilters = (
  signing: ListSigningDto,
  filters: SigningFilter[]
): boolean => {
  if (filters.length === 0) {
    return true;
  }
  return filters.some((filter) => {
    switch (filter) {
      case 'draft':
        return signing.state === 'DRAFT';
      case 'pending':
        return (
          signing.state === 'IN_REVIEW' ||
          signing.state === 'STARTING' ||
          signing.state === 'SIGNING'
        );
      case 'expired':
        return signing.state === 'EXPIRED';
      case 'cancelled':
        return signing.state === 'CANCELLED';
      case 'done':
        return (
          signing.state !== 'DRAFT' &&
          signing.state !== 'IN_REVIEW' &&
          signing.state !== 'STARTING' &&
          signing.state !== 'SIGNING' &&
          signing.state !== 'EXPIRED' &&
          signing.state !== 'CANCELLED'
        );
      default:
        return false;
    }
  });
};

export default FilterPills;
